import { fetchClient } from ".";
import { CreateUserParams } from "../types/types";

export const auth = async (username: string, password: string, twoFactorCode?: string) => {
  try {
    const response = await fetchClient("/api/v1/token", {
      method: "POST",
      body: JSON.stringify({ username, password, two_factor_code: twoFactorCode || null }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      const error = new Error(payload.detail || `Login failed: ${response.statusText}`) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    if (!data.authenticated) throw new Error("Authentication was not completed");
    return data;
  } catch (error) {
    console.error("Auth error:", error);
    throw error;
  }
};

export const check = async () => {
  try {
    await fetchUserInfo();
    return true;
  } catch (error) {
    console.error("Check auth error:", error);
    return false;
  }
};

export const fetchUserInfo = async () => {
  try {
    const response = await fetchClient("/api/v1/profile/user", {
      method: "GET",
      auth: true,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user info: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Fetch user info error:", error);
    throw error;
  }
};

export const createUser = async (user: CreateUserParams) => {
  try {
    const response = await fetchClient("/api/v1/register", {
      method: "POST",
      body: JSON.stringify(user),
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw { status: response.status, data };
    }

    return data;
  } catch (error) {
    throw error;
  }
};

export const sendEmailResetPassword = async (email: string) => {
  try {
    const response = await fetchClient("/api/v1/password/reset/request", {
      method: "POST",
      body: JSON.stringify({ email }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Login failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Auth error:", error);
    throw error;
  }
};

export const sendCodeResetPassword = async (email: string, code: string) => {
  try {
    const response = await fetchClient("/api/v1/password/reset/verify", {
      method: "POST",
      body: JSON.stringify({ email, reset_code: code }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Code failed: ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.valid) throw new Error("The reset code is invalid or expired");
    return data;
  } catch (error) {
    console.error("Auth error:", error);
    throw error;
  }
};

export const sendNewResetPassword = async (
  email: string,
  code: string,
  password: string
) => {
  try {
    const response = await fetchClient("/api/v1/password/reset/confirm", {
      method: "POST",
      body: JSON.stringify({ email, reset_code: code, new_password: password }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Code failed: ${response.statusText}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Auth error:", error);
    throw error;
  }
};
